import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe, CheckCircle, ArrowRight, RefreshCw, MapPin, Tag, Play, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/components/AuthProvider';

interface ScrapedBusiness {
  id: string;
  name: string;
  description?: string;
  category?: string;
  address?: string;
  city?: string;
  state?: string;
  phone?: string;
  website?: string;
  source: string;
  sourceUrl?: string;
  verified: boolean;
  rating?: number;
  reviewCount?: number;
  yelpId?: string;
  scrapedAt?: string;
}

const ROLLOUT_PHASES = [
  {
    phase: 1,
    label: 'Phase 1 — Louisiana',
    color: 'purple',
    cities: [
      { city: 'New Orleans', state: 'LA' },
      { city: 'Baton Rouge', state: 'LA' },
      { city: 'Lafayette', state: 'LA' },
      { city: 'Alexandria', state: 'LA' },
      { city: 'Shreveport', state: 'LA' },
      { city: 'Lake Charles', state: 'LA' },
      { city: 'Monroe', state: 'LA' },
    ]
  },
  {
    phase: 2,
    label: 'Phase 2 — Mississippi',
    color: 'blue',
    cities: [
      { city: 'Jackson', state: 'MS' },
      { city: 'Hattiesburg', state: 'MS' },
      { city: 'Biloxi', state: 'MS' },
      { city: 'Gulfport', state: 'MS' },
      { city: 'Meridian', state: 'MS' },
      { city: 'Greenville', state: 'MS' },
    ]
  },
  {
    phase: 3,
    label: 'Phase 3 — Alabama',
    color: 'red',
    cities: [
      { city: 'Birmingham', state: 'AL' },
      { city: 'Montgomery', state: 'AL' },
      { city: 'Mobile', state: 'AL' },
      { city: 'Huntsville', state: 'AL' },
      { city: 'Tuscaloosa', state: 'AL' },
      { city: 'Selma', state: 'AL' },
    ]
  },
  {
    phase: 4,
    label: 'Phase 4 — Arkansas',
    color: 'green',
    cities: [
      { city: 'Little Rock', state: 'AR' },
      { city: 'Pine Bluff', state: 'AR' },
      { city: 'Fort Smith', state: 'AR' },
      { city: 'Fayetteville', state: 'AR' },
      { city: 'Jonesboro', state: 'AR' },
    ]
  },
  {
    phase: 5,
    label: 'Phase 5 — Tennessee',
    color: 'orange',
    cities: [
      { city: 'Nashville', state: 'TN' },
      { city: 'Memphis', state: 'TN' },
      { city: 'Knoxville', state: 'TN' },
      { city: 'Chattanooga', state: 'TN' },
      { city: 'Clarksville', state: 'TN' },
    ]
  },
  {
    phase: 6,
    label: 'Phase 6 — The Carolinas',
    color: 'cyan',
    cities: [
      { city: 'Charlotte', state: 'NC' },
      { city: 'Raleigh', state: 'NC' },
      { city: 'Durham', state: 'NC' },
      { city: 'Greensboro', state: 'NC' },
      { city: 'Fayetteville', state: 'NC' },
      { city: 'Columbia', state: 'SC' },
      { city: 'Charleston', state: 'SC' },
      { city: 'Greenville', state: 'SC' },
    ]
  },
  {
    phase: 7,
    label: 'Phase 7 — Kentucky',
    color: 'indigo',
    cities: [
      { city: 'Louisville', state: 'KY' },
      { city: 'Lexington', state: 'KY' },
      { city: 'Bowling Green', state: 'KY' },
    ]
  },
  {
    phase: 8,
    label: 'Phase 8 — Major Cities',
    color: 'yellow',
    cities: [
      { city: 'Atlanta', state: 'GA' },
      { city: 'Jacksonville', state: 'FL' },
      { city: 'Houston', state: 'TX' },
      { city: 'Dallas', state: 'TX' },
      { city: 'Tampa', state: 'FL' },
      { city: 'Miami', state: 'FL' },
      { city: 'New York', state: 'NY' },
      { city: 'Chicago', state: 'IL' },
      { city: 'Detroit', state: 'MI' },
      { city: 'Baltimore', state: 'MD' },
      { city: 'Philadelphia', state: 'PA' },
      { city: 'Washington', state: 'DC' },
    ]
  },
];

const CATEGORIES = [
  { label: 'All Categories', value: 'All' },
  { label: 'Restaurant', value: 'Restaurant' },
  { label: 'Beauty & Wellness', value: 'Beauty & Wellness' },
  { label: 'Professional Services', value: 'Professional Services' },
  { label: 'Retail', value: 'Retail' },
  { label: 'Health & Medical', value: 'Health & Medical' },
  { label: 'Automotive', value: 'Automotive' },
  { label: 'Real Estate', value: 'Real Estate' },
  { label: 'Technology', value: 'Technology' },
  { label: 'Entertainment', value: 'Entertainment' },
  { label: 'Home Services', value: 'Home Services' },
  { label: 'Financial Services', value: 'Financial Services' },
  { label: 'Legal Services', value: 'Legal Services' },
];

const PHASE_COLORS: Record<string, string> = {
  purple: 'bg-purple-900/30 border-purple-700/50 text-purple-300',
  blue: 'bg-blue-900/30 border-blue-700/50 text-blue-300',
  red: 'bg-red-900/30 border-red-700/50 text-red-300',
  green: 'bg-blue-950/30 border-red-800/50 text-green-300',
  orange: 'bg-orange-900/30 border-orange-700/50 text-orange-300',
  cyan: 'bg-cyan-900/30 border-cyan-700/50 text-cyan-300',
  indigo: 'bg-indigo-900/30 border-indigo-700/50 text-indigo-300',
  yellow: 'bg-yellow-900/30 border-yellow-700/50 text-yellow-300',
};

export default function AdminScrapingPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedCity, setSelectedCity] = useState<{ city: string; state: string } | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedPhase, setExpandedPhase] = useState<number>(1);
  const [scrapeLog, setScrapeLog] = useState<string[]>([]);
  const [scrapeResults, setScrapeResults] = useState<ScrapedBusiness[]>([]);
  const [apiKeyMissing, setApiKeyMissing] = useState(false);

  if (!user || (user.email !== 'admin@blackbook.com' && user.email !== 'sam@truckwithchuck.com')) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-400">Admin permissions required.</p>
        </div>
      </div>
    );
  }

  const { data: allScraped = [], isLoading } = useQuery<ScrapedBusiness[]>({
    queryKey: ['/api/scraped-businesses'],
    queryFn: () => apiRequest('/api/scraped-businesses'),
  });

  const yelpScrapeMutation = useMutation({
    mutationFn: ({ city, state, category }: { city: string; state: string; category: string }) =>
      apiRequest('/api/yelp-scrape', {
        method: 'POST',
        body: JSON.stringify({ city, state, category }),
      }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/scraped-businesses'] });
      const businesses = data.businesses || [];
      setScrapeResults(prev => [...businesses, ...prev]);
      setScrapeLog(prev => [
        `✅ ${selectedCity?.city}, ${selectedCity?.state}: Found ${businesses.length} Black-owned businesses`,
        ...prev,
      ]);
      setApiKeyMissing(false);
    },
    onError: (err: any) => {
      const msg = err?.message || String(err);
      if (msg.includes('YELP_API_KEY')) {
        setApiKeyMissing(true);
        setScrapeLog(prev => [`❌ Yelp API key not configured — add YELP_API_KEY to secrets`, ...prev]);
      } else {
        setScrapeLog(prev => [`❌ Error for ${selectedCity?.city}: ${msg}`, ...prev]);
      }
    },
  });

  const promoteBusinessMutation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiRequest(`/api/scraped-businesses/${id}/promote`, {
        method: 'POST',
        body: JSON.stringify({ ownerId: 'admin' }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/scraped-businesses'] });
      queryClient.invalidateQueries({ queryKey: ['/api/businesses'] });
    },
  });

  const handleScrape = () => {
    if (!selectedCity) return;
    setScrapeLog(prev => [`⏳ Scraping ${selectedCity.city}, ${selectedCity.state} — ${selectedCategory}...`, ...prev]);
    yelpScrapeMutation.mutate({
      city: selectedCity.city,
      state: selectedCity.state,
      category: selectedCategory,
    });
  };

  const cityCounts = scrapeResults.reduce((acc, b) => {
    const key = `${b.city}, ${b.state}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="min-h-screen bg-gray-950 py-8 px-4">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Directory Scraping</h1>
          <p className="text-gray-400">Build the BlackBook directory city by city using the Yelp Black-Owned filter.</p>
        </div>

        {/* API Key Warning */}
        {apiKeyMissing && (
          <div className="mb-6 bg-amber-900/30 border border-amber-700/50 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 font-medium mb-1">Yelp API Key Required</p>
              <p className="text-amber-400/80 text-sm mb-2">
                To start scraping real business data, you need a free Yelp Fusion API key.
              </p>
              <ol className="text-sm text-amber-400/70 space-y-1 list-decimal list-inside">
                <li>Go to <a href="https://www.yelp.com/developers/v3/manage_app" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-300">yelp.com/developers</a> and create a free app</li>
                <li>Copy your API Key</li>
                <li>In Replit, go to Secrets and add: <code className="bg-black/30 px-1.5 py-0.5 rounded font-mono text-xs">YELP_API_KEY</code></li>
                <li>Restart the server, then try scraping again</li>
              </ol>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left — City Selector */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-gray-800">
                <h2 className="font-semibold text-white flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-purple-400" />
                  Rollout Plan
                </h2>
                <p className="text-xs text-gray-500 mt-1">Click a city to select it for scraping</p>
              </div>

              <div className="divide-y divide-gray-800/50">
                {ROLLOUT_PHASES.map(phase => (
                  <div key={phase.phase}>
                    <button
                      onClick={() => setExpandedPhase(expandedPhase === phase.phase ? 0 : phase.phase)}
                      className="w-full flex items-center justify-between p-3 hover:bg-gray-800/50 transition-colors text-left"
                    >
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${PHASE_COLORS[phase.color]}`}>
                        {phase.label}
                      </span>
                      {expandedPhase === phase.phase
                        ? <ChevronDown className="h-4 w-4 text-gray-500" />
                        : <ChevronRight className="h-4 w-4 text-gray-500" />
                      }
                    </button>

                    {expandedPhase === phase.phase && (
                      <div className="pb-2">
                        {phase.cities.map(loc => {
                          const key = `${loc.city}, ${loc.state}`;
                          const count = cityCounts[key] || 0;
                          const isSelected = selectedCity?.city === loc.city && selectedCity?.state === loc.state;
                          return (
                            <button
                              key={key}
                              onClick={() => setSelectedCity(loc)}
                              className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors ${
                                isSelected
                                  ? 'bg-purple-900/40 text-purple-300'
                                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                              }`}
                            >
                              <span>{loc.city}, {loc.state}</span>
                              {count > 0 && (
                                <span className="text-xs bg-blue-950/50 text-red-500 border border-red-800/50 px-1.5 py-0.5 rounded-full">
                                  {count}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right — Scrape Controls + Results */}
          <div className="lg:col-span-2 space-y-4">

            {/* Scrape Controls */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Globe className="h-4 w-4 text-red-500" />
                Yelp Scraper
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Selected city */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wide">Selected City</label>
                  <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm ${
                    selectedCity
                      ? 'bg-purple-900/20 border-purple-700/50 text-purple-300'
                      : 'bg-gray-800 border-gray-700 text-gray-500'
                  }`}>
                    <MapPin className="h-4 w-4 flex-shrink-0" />
                    {selectedCity ? `${selectedCity.city}, ${selectedCity.state}` : 'Select a city from the rollout plan'}
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wide">Category</label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <select
                      value={selectedCategory}
                      onChange={e => setSelectedCategory(e.target.value)}
                      className="w-full pl-9 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 appearance-none"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <button
                onClick={handleScrape}
                disabled={!selectedCity || yelpScrapeMutation.isPending}
                className="flex items-center gap-2 px-5 py-2.5 bg-red-700 text-white rounded-lg hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors"
              >
                {yelpScrapeMutation.isPending
                  ? <><RefreshCw className="h-4 w-4 animate-spin" /> Scraping Yelp...</>
                  : <><Play className="h-4 w-4" /> Scrape {selectedCity ? `${selectedCity.city}` : 'City'}</>
                }
              </button>

              <p className="text-xs text-gray-600 mt-2">
                Uses Yelp's <code className="text-gray-500">black_owned</code> attribute filter. Free tier: 500 calls/day.
              </p>
            </div>

            {/* Activity Log */}
            {scrapeLog.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-white mb-3">Activity Log</h3>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {scrapeLog.map((log, i) => (
                    <p key={i} className={`text-xs font-mono ${
                      log.startsWith('✅') ? 'text-red-500'
                      : log.startsWith('❌') ? 'text-red-400'
                      : 'text-gray-400'
                    }`}>
                      {log}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-white">{allScraped.length + scrapeResults.length}</div>
                <div className="text-xs text-gray-500 mt-0.5">Total Scraped</div>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-red-500">{Object.keys(cityCounts).length}</div>
                <div className="text-xs text-gray-500 mt-0.5">Cities Covered</div>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-400">
                  {allScraped.filter(b => b.verified).length}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">Verified</div>
              </div>
            </div>

            {/* Recent Results */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-gray-800">
                <h2 className="font-semibold text-white">Scraped Businesses</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {scrapeResults.length > 0 ? `${scrapeResults.length} from this session` : 'Showing all stored results'}
                </p>
              </div>

              {isLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">Loading...</p>
                </div>
              ) : (allScraped.length === 0 && scrapeResults.length === 0) ? (
                <div className="p-10 text-center">
                  <Globe className="mx-auto h-10 w-10 text-gray-700 mb-3" />
                  <p className="text-gray-500 text-sm">No businesses scraped yet.</p>
                  <p className="text-gray-600 text-xs mt-1">Select a city and click Scrape to get started.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-800/50 max-h-[500px] overflow-y-auto">
                  {[...scrapeResults, ...allScraped].map((business, idx) => (
                    <div key={business.id || idx} className="p-4 hover:bg-gray-800/30 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-white text-sm truncate">{business.name}</h3>
                            {business.verified && <CheckCircle className="h-3.5 w-3.5 text-red-600 flex-shrink-0" />}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
                            {business.category && <span>{business.category}</span>}
                            {business.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{business.city}, {business.state}</span>}
                            {business.phone && <span>{business.phone}</span>}
                            {(business as any).rating && <span>★ {(business as any).rating}</span>}
                          </div>
                          <p className="text-xs text-gray-600 mt-0.5">via {business.source}</p>
                        </div>
                        <button
                          onClick={() => promoteBusinessMutation.mutate({ id: business.id })}
                          disabled={promoteBusinessMutation.isPending}
                          className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 bg-red-800 hover:bg-red-700 text-white rounded-md text-xs font-medium transition-colors disabled:opacity-50"
                        >
                          <ArrowRight className="h-3.5 w-3.5" />
                          Promote
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
